'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';

export type Lang = 'ru' | 'en' | 'uz';

interface LangContextType {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => string;
}

// ── Translation dictionary ──────────────────────────────────────────────────
const translations: Record<Lang, Record<string, string>> = {
  ru: {
    // Nav
    nav_home: 'Главная',
    nav_destinations: 'Направления',
    nav_uzbekistan: 'Узбекистан',
    nav_about: 'О нас',
    nav_contact: 'Контакты',
    nav_book: 'Забронировать',

    // Hero
    hero_label: 'Туристическое агентство',
    hero_title: 'Откройте мир\nвместе с нами',
    hero_subtitle: 'Незабываемые туры в Дубай, Самарканд, Бухару и другие жемчужины мира. Мы создаём путешествия вашей мечты.',
    hero_cta: 'Смотреть туры',
    hero_cta2: 'Связаться с нами',
    hero_stat1_num: '5000+',
    hero_stat1_label: 'Довольных туристов',
    hero_stat2_num: '20+',
    hero_stat2_label: 'Направлений',
    hero_stat3_num: '10+',
    hero_stat3_label: 'Лет опыта',

    // Marquee
    marquee: 'Дубай • Самарканд • Бухара • Стамбул • Мальдивы • Париж • Рим • Барселона •',

    // Destinations
    dest_label: 'Направления',
    dest_title: 'Куда отправиться?',
    dest_subtitle: 'Откройте для себя самые удивительные уголки мира — от сказочных дворцов Востока до современных небоскрёбов Дубая.',
    dest_dubai: 'Дубай',
    dest_dubai_desc: 'Город будущего с золотыми пляжами и небоскрёбами',
    dest_samarkand: 'Самарканд',
    dest_samarkand_desc: 'Жемчужина Шёлкового пути — тысячелетняя история',
    dest_bukhara: 'Бухара',
    dest_bukhara_desc: 'Священный город медресе и старинных базаров',
    dest_istanbul: 'Стамбул',
    dest_istanbul_desc: 'Мост между Европой и Азией',
    dest_maldives: 'Мальдивы',
    dest_maldives_desc: 'Кристальные лагуны и коралловые рифы',
    dest_paris: 'Париж',
    dest_paris_desc: 'Город любви, моды и высокой кухни',
    dest_explore: 'Подробнее',

    // Uzbekistan
    uzbek_label: 'Узбекистан',
    uzbek_title: 'Сердце Великого Шёлкового пути',
    uzbek_desc: 'Узбекистан — страна, где каждый камень хранит тысячелетнюю историю. Здесь мечети и медресе соперничают красотой с лучшими памятниками мира, а гостеприимство народа не знает границ.',
    uzbek_samarkand_title: 'Самарканд',
    uzbek_samarkand_desc: 'Одни из самых красивых памятников исламской архитектуры в мире. Площадь Регистан, мавзолей Гур-э-Амир, обсерватория Улугбека — всё это ждёт вас в Самарканде.',
    uzbek_samarkand_fact1: 'Площадь Регистан — UNESCO',
    uzbek_samarkand_fact2: '2750 лет истории',
    uzbek_samarkand_fact3: 'Родина Тамерлана',
    uzbek_bukhara_title: 'Бухара',
    uzbek_bukhara_desc: 'Один из самых хорошо сохранившихся средневековых городов в Центральной Азии. Минарет Калян, цитадель Арк, медресе Мири-Ариб создают атмосферу сказки «Тысячи и одной ночи».',
    uzbek_bukhara_fact1: 'Старый город — UNESCO',
    uzbek_bukhara_fact2: 'Более 140 памятников',
    uzbek_bukhara_fact3: '2500 лет истории',
    uzbek_cta: 'Узнать о турах в Узбекистан',

    // Why us
    why_label: 'Почему мы',
    why_title: 'Ваш надёжный\nпутеводитель',
    why_1_title: 'Опытные гиды',
    why_1_desc: 'Наши местные гиды знают каждый уголок — от скрытых двориков Бухары до секретных пляжей Дубая.',
    why_2_title: 'Лучшие цены',
    why_2_desc: 'Мы работаем напрямую с отелями и авиакомпаниями, предлагая самые выгодные пакеты без скрытых наценок.',
    why_3_title: 'Поддержка 24/7',
    why_3_desc: 'Наша команда всегда на связи — в любое время суток мы готовы помочь решить любой вопрос.',
    why_4_title: 'Индивидуальные туры',
    why_4_desc: 'Каждое путешествие создаётся под ваши пожелания: даты, маршрут, уровень комфорта — всё по вашему выбору.',
    why_5_title: 'Визовая поддержка',
    why_5_desc: 'Помогаем оформить визы и все необходимые документы быстро и без лишних хлопот.',
    why_6_title: 'Безопасность',
    why_6_desc: 'Все наши туры включают страховку и тщательно проверенных партнёров в каждой стране.',

    // Contact
    contact_label: 'Контакты',
    contact_title: 'Готовы\nотправиться?',
    contact_subtitle: 'Свяжитесь с нами любым удобным способом — мы ответим быстро и поможем спланировать идеальное путешествие.',
    contact_phone: 'Позвонить нам',
    contact_telegram: 'Написать в Telegram',
    contact_instagram: 'Instagram',
    contact_hours: 'Пн–Вс: 9:00 – 21:00',
    contact_form_name: 'Ваше имя',
    contact_form_phone: 'Телефон',
    contact_form_dest: 'Желаемое направление',
    contact_form_submit: 'Отправить запрос',
    contact_form_success: 'Спасибо! Мы свяжемся с вами в ближайшее время.',

    // Footer
    footer_tagline: 'Создаём незабываемые путешествия с 2014 года',
    footer_links: 'Навигация',
    footer_social: 'Соцсети',
    footer_legal: '© 2026 AirDubaiTravel. Все права защищены.',
  },

  en: {
    nav_home: 'Home',
    nav_destinations: 'Destinations',
    nav_uzbekistan: 'Uzbekistan',
    nav_about: 'About',
    nav_contact: 'Contact',
    nav_book: 'Book Now',

    hero_label: 'Travel Agency',
    hero_title: 'Discover the World\nWith Us',
    hero_subtitle: 'Unforgettable tours to Dubai, Samarkand, Bukhara and other gems of the world. We craft journeys of your dreams.',
    hero_cta: 'Explore Tours',
    hero_cta2: 'Contact Us',
    hero_stat1_num: '5000+',
    hero_stat1_label: 'Happy Travelers',
    hero_stat2_num: '20+',
    hero_stat2_label: 'Destinations',
    hero_stat3_num: '10+',
    hero_stat3_label: 'Years Experience',

    marquee: 'Dubai • Samarkand • Bukhara • Istanbul • Maldives • Paris • Rome • Barcelona •',

    dest_label: 'Destinations',
    dest_title: 'Where to go?',
    dest_subtitle: 'Discover the most amazing corners of the world — from the fairy-tale palaces of the East to the modern skyscrapers of Dubai.',
    dest_dubai: 'Dubai',
    dest_dubai_desc: 'City of the future with golden beaches and skyscrapers',
    dest_samarkand: 'Samarkand',
    dest_samarkand_desc: 'Pearl of the Silk Road — millennia of history',
    dest_bukhara: 'Bukhara',
    dest_bukhara_desc: 'Sacred city of madrasahs and ancient bazaars',
    dest_istanbul: 'Istanbul',
    dest_istanbul_desc: 'Bridge between Europe and Asia',
    dest_maldives: 'Maldives',
    dest_maldives_desc: 'Crystal lagoons and coral reefs',
    dest_paris: 'Paris',
    dest_paris_desc: 'City of love, fashion and haute cuisine',
    dest_explore: 'Explore',

    uzbek_label: 'Uzbekistan',
    uzbek_title: 'Heart of the Great Silk Road',
    uzbek_desc: 'Uzbekistan is a land where every stone holds thousands of years of history. Mosques and madrasahs rival the beauty of the world\'s finest monuments, and the hospitality of its people knows no bounds.',
    uzbek_samarkand_title: 'Samarkand',
    uzbek_samarkand_desc: 'Some of the most beautiful examples of Islamic architecture in the world. Registan Square, Gur-e-Amir mausoleum, Ulugbek Observatory — all await you in Samarkand.',
    uzbek_samarkand_fact1: 'Registan Square — UNESCO',
    uzbek_samarkand_fact2: '2750 years of history',
    uzbek_samarkand_fact3: 'Birthplace of Tamerlane',
    uzbek_bukhara_title: 'Bukhara',
    uzbek_bukhara_desc: 'One of the best-preserved medieval cities in Central Asia. The Kalon Minaret, Ark Citadel, and Miri-Arab Madrassa create an atmosphere straight from One Thousand and One Nights.',
    uzbek_bukhara_fact1: 'Old City — UNESCO',
    uzbek_bukhara_fact2: 'Over 140 monuments',
    uzbek_bukhara_fact3: '2500 years of history',
    uzbek_cta: 'Uzbekistan Tour Packages',

    why_label: 'Why Us',
    why_title: 'Your Trusted\nTravel Guide',
    why_1_title: 'Expert Guides',
    why_1_desc: 'Our local guides know every corner — from the hidden courtyards of Bukhara to the secret beaches of Dubai.',
    why_2_title: 'Best Prices',
    why_2_desc: 'We work directly with hotels and airlines, offering the most competitive packages with no hidden fees.',
    why_3_title: '24/7 Support',
    why_3_desc: 'Our team is always available — at any hour, ready to help resolve any issue.',
    why_4_title: 'Custom Tours',
    why_4_desc: 'Every journey is tailored to your wishes: dates, itinerary, comfort level — all your choice.',
    why_5_title: 'Visa Assistance',
    why_5_desc: 'We help arrange visas and all necessary documents quickly and hassle-free.',
    why_6_title: 'Safety First',
    why_6_desc: 'All our tours include travel insurance and carefully vetted partners in every country.',

    contact_label: 'Contact',
    contact_title: 'Ready to\nExplore?',
    contact_subtitle: 'Reach out in any convenient way — we respond fast and help plan the perfect trip.',
    contact_phone: 'Call Us',
    contact_telegram: 'Message on Telegram',
    contact_instagram: 'Instagram',
    contact_hours: 'Mon–Sun: 9:00 – 21:00',
    contact_form_name: 'Your Name',
    contact_form_phone: 'Phone Number',
    contact_form_dest: 'Desired Destination',
    contact_form_submit: 'Send Request',
    contact_form_success: 'Thank you! We will contact you shortly.',

    footer_tagline: 'Crafting unforgettable journeys since 2014',
    footer_links: 'Navigation',
    footer_social: 'Social',
    footer_legal: '© 2026 AirDubaiTravel. All rights reserved.',
  },

  uz: {
    nav_home: 'Bosh sahifa',
    nav_destinations: 'Yo\'nalishlar',
    nav_uzbekistan: 'O\'zbekiston',
    nav_about: 'Biz haqimizda',
    nav_contact: 'Aloqa',
    nav_book: 'Band qilish',

    hero_label: 'Sayohat agentligi',
    hero_title: 'Dunyoni biz bilan\nkashf eting',
    hero_subtitle: 'Dubay, Samarqand, Buxoro va dunyo mo\'jizalariga unutilmas sayohatlar. Biz sizning orzuingizdagi sayohatni yaratamiz.',
    hero_cta: 'Turlarni ko\'rish',
    hero_cta2: 'Biz bilan bog\'laning',
    hero_stat1_num: '5000+',
    hero_stat1_label: 'Mamnun sayohatchilar',
    hero_stat2_num: '20+',
    hero_stat2_label: 'Yo\'nalishlar',
    hero_stat3_num: '10+',
    hero_stat3_label: 'Yillik tajriba',

    marquee: 'Dubay • Samarqand • Buxoro • Istanbul • Maldiv • Parij • Rim • Barselona •',

    dest_label: 'Yo\'nalishlar',
    dest_title: 'Qayerga borish?',
    dest_subtitle: 'Dunyoning eng ajoyib burchaklarini kashf eting — Sharqning ertak saroylaridan Dubayning zamonaviy osmono\'parlarigacha.',
    dest_dubai: 'Dubay',
    dest_dubai_desc: 'Oltin plyajlar va osmono\'parlar shahri',
    dest_samarkand: 'Samarqand',
    dest_samarkand_desc: 'Ipak yo\'lining gavhari — ming yillik tarix',
    dest_bukhara: 'Buxoro',
    dest_bukhara_desc: 'Madrasalar va qadimiy bozorlarning muqaddas shahri',
    dest_istanbul: 'Istanbul',
    dest_istanbul_desc: 'Yevropa va Osiyo o\'rtasidagi ko\'prik',
    dest_maldives: 'Maldiv',
    dest_maldives_desc: 'Kristall lagunalar va marjon riflari',
    dest_paris: 'Parij',
    dest_paris_desc: 'Muhabbat, moda va yuqori oshpazlik shahri',
    dest_explore: 'Batafsil',

    uzbek_label: 'O\'zbekiston',
    uzbek_title: 'Buyuk Ipak yo\'lining yuragi',
    uzbek_desc: 'O\'zbekiston — har bir toshi ming yillik tarixni saqlaydigan mamlakat. Bu yerda masjid va madrasalar dunyo yodgorliklarining go\'zalligi bilan raqobatlashadi, xalqning mehmondo\'stligi esa chegarasizdir.',
    uzbek_samarkand_title: 'Samarqand',
    uzbek_samarkand_desc: 'Dunyodagi eng go\'zal islom me\'morchiligining namunalari. Registon maydoni, G\'ur-Amir maqbarasi, Ulug\'bek rasadxonasi — bularning barchasi Samarqandda sizni kutmoqda.',
    uzbek_samarkand_fact1: 'Registon — UNESCO',
    uzbek_samarkand_fact2: '2750 yillik tarix',
    uzbek_samarkand_fact3: 'Amir Temur vatani',
    uzbek_bukhara_title: 'Buxoro',
    uzbek_bukhara_desc: 'Markaziy Osiyodagi eng yaxshi saqlanib qolgan o\'rta asr shaharlaridan biri. Kalon minorasi, Ark qal\'asi, Mir Arab madrasasi — "Ming bir kecha" ertagining muhitini yaratadi.',
    uzbek_bukhara_fact1: 'Eski shahar — UNESCO',
    uzbek_bukhara_fact2: '140 dan ortiq yodgorliklar',
    uzbek_bukhara_fact3: '2500 yillik tarix',
    uzbek_cta: 'O\'zbekiston turlari',

    why_label: 'Nega biz',
    why_title: 'Ishonchli\nyo\'lboshchingiz',
    why_1_title: 'Tajribali gidlar',
    why_1_desc: 'Mahalliy gidlarimiz har bir burchakni bilishadi — Buxoroning yashirin hovlillaridan Dubayning maxfiy plyajlarigacha.',
    why_2_title: 'Eng yaxshi narxlar',
    why_2_desc: 'Biz mehmonxonalar va aviakompaniyalar bilan to\'g\'ridan-to\'g\'ri ishlaymiz, yashirin to\'lovsiz eng foydali paketlarni taklif etamiz.',
    why_3_title: '24/7 qo\'llab-quvvatlash',
    why_3_desc: 'Jamoamiz doimo aloqada — kunning istalgan vaqtida har qanday masalani hal etishga tayyormiz.',
    why_4_title: 'Individual turlar',
    why_4_desc: 'Har bir sayohat sizning istaklaringizga ko\'ra yaratiladi: sanalar, marshrut, qulaylik darajasi — hammasi sizning tanlovingiz.',
    why_5_title: 'Viza yordami',
    why_5_desc: 'Vizalar va barcha kerakli hujjatlarni tez va muammosiz rasmiylashtirish uchun yordam beramiz.',
    why_6_title: 'Xavfsizlik',
    why_6_desc: 'Barcha turlarimiz sug\'urta va har bir mamlakatda sinchkovlik bilan tekshirilgan hamkorlarni o\'z ichiga oladi.',

    contact_label: 'Aloqa',
    contact_title: 'Sayohatga\ntayyormisiz?',
    contact_subtitle: 'Qulay usulda biz bilan bog\'laning — tez javob beramiz va ideal sayohatni rejalashtirish uchun yordam beramiz.',
    contact_phone: 'Qo\'ng\'iroq qiling',
    contact_telegram: 'Telegram orqali yozing',
    contact_instagram: 'Instagram',
    contact_hours: 'Du–Ya: 9:00 – 21:00',
    contact_form_name: 'Ismingiz',
    contact_form_phone: 'Telefon',
    contact_form_dest: 'Kerakli yo\'nalish',
    contact_form_submit: 'So\'rov yuborish',
    contact_form_success: 'Rahmat! Tez orada siz bilan bog\'lanamiz.',

    footer_tagline: '2014 yildan beri unutilmas sayohatlar yaratamiz',
    footer_links: 'Navigatsiya',
    footer_social: 'Ijtimoiy tarmoqlar',
    footer_legal: '© 2026 AirDubaiTravel. Barcha huquqlar himoyalangan.',
  },
};

const LangContext = createContext<LangContextType>({
  lang: 'ru',
  setLang: () => {},
  t: (k) => k,
});

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>('ru');

  const t = (key: string): string => {
    return translations[lang][key] ?? translations['ru'][key] ?? key;
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}